import { Button, Toolbar, VSpacer } from '@/components/DesignSystem';
import { Filter, FilterSelections } from '@/components/DesignSystem/Toolbar/interfaces';
import { Action } from '@/components/shared/ListSelector/helper';
import { ProductCard } from '@/components/shared/ListSelector/ProductCard';
import { QueryKeys } from '@/constants/QueryKeys';
import { useCategoryList, useManufacturerList } from '@/hooks/useProductQuery';
import { useSearch } from '@/hooks/useSearch';
import { useSnackbar } from '@/providers/GlobalSnackbarProvider';
import { ProductApi } from '@/utilities/api/ProductApi';
import { PromotionApi } from '@/utilities/api/PromotionApi';
import { ProductEndpoint } from '@api/endpoints';
import { ApiProduct, ApiPromotion, ApiRetailer } from '@api/interfaces';
import { CircularProgress, Container, Pagination, Stack } from '@mui/material';
import { SharedConfig } from '@shared/SharedConfig';
import { businessToday, ConversionUtility } from '@shared/utilities';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import AddIcon from "@mui/icons-material/Add";
import GroupIcon from '@/assets/icons/group.svg';
import { RewardsUom } from "@shared/enums/RewardsUom";

interface ProductListProps {
  disabled?: boolean,
  onChange: (updatedIds: string[]) => void,
  requiredManufacturerID?: string,
  requiredUOM?: RewardsUom,
  selectedIds: string[],
  selectedRetailer: ApiRetailer,
  onAddedProductsButtonClicked?: () => void,
}

export const ProductList = ({
  disabled,
  onChange,
  requiredUOM,
  requiredManufacturerID,
  selectedIds,
  selectedRetailer,
  onAddedProductsButtonClicked,
}: ProductListProps) => {
  const { t } = useTranslation();
  const [filterSelections, setFilterSelections] = useState<FilterSelections>(
    () => new Map(),
  );
  const [page, setPage] = useState(0);
  const { setSearch, debouncedSearch } = useSearch(3);

  const { categoryList } = useCategoryList({
    ...(selectedRetailer && { retailerId: selectedRetailer.id }),
  });

  const { manufacturerList } = useManufacturerList({
    ...(selectedRetailer && { retailerId: selectedRetailer.id }),
  }, {
    enabled: !requiredManufacturerID,
  });

  const categoryIdFilterSelections = Array.from(filterSelections?.get('category-id') ?? []);
  const manufacturerIdFilterSelections = Array.from(filterSelections?.get('manufacturer-id') ?? []);
  const subcategoryIdFilterSelections = Array.from(filterSelections?.get('subcategory-id') ?? []);

  const { openSnackbar } = useSnackbar();
  const [isAddingAll, setIsAddingAll] = useState(false);

  const categoryIdFilterSelectionsString = JSON.stringify(categoryIdFilterSelections);
  useEffect(() => {
    setFilterSelections((filterSelections) => {
      filterSelections.set('subcategory-id', new Set());
      return filterSelections;
    });
  }, [categoryIdFilterSelectionsString]);

  const query: ProductEndpoint.ProductList.Query = {
    categoryId: categoryIdFilterSelections,
    hasExternalId: !!selectedRetailer,
    manufacturerId: requiredManufacturerID
      ? [requiredManufacturerID]
      : manufacturerIdFilterSelections,
    page,
    retailerId: [selectedRetailer.id],
    search: debouncedSearch,
    subCategories: subcategoryIdFilterSelections,
  };

  const { data: products, isLoading } = useQuery(
    [QueryKeys.GET_PRODUCT, query],
    () => ProductApi.productListData(query),
  );

  const productIds = products?.data.map((product) => product.id);
  const { data: promotions } = useQuery(
    [QueryKeys.GET_PROMOTIONS, productIds],
    async () => PromotionApi.list({
      isActive: true,
      productIds: productIds,
    }),
    {
      enabled: !!productIds?.length,
    },
  );

  const currentDate = businessToday();

  const getPromotions = useCallback((productId: string, promotions: ApiPromotion[]) => (
    promotions.filter(
      (promotion) => (
        promotion.productIdsForEarningPoints.includes(productId)
        && promotion.endDate >= currentDate
      ),
    ) ?? []
  ), [currentDate]);

  const categoryOptions = categoryList?.map((category) => ({
    id: category.id,
    label: category.name,
  })).sort((a, b) => a.label.localeCompare(b.label)) ?? [];

  const addAll = async () => {
    if ((products?.total ?? 0) > SharedConfig.maxPageLimit) {
      openSnackbar('Too many products. Please filter down the list.');
      return;
    }
    setIsAddingAll(true);
    const { data: fullListOfProducts } = await ProductApi.productListData(
      { ...query, limit: SharedConfig.maxPageLimit },
    );
    const productIdsToAdd = fullListOfProducts
      .filter(isAvailable)
      .map(p => p.id);

    const updatedIdList = new Set([...selectedIds, ...productIdsToAdd]);
    onChange(Array.from(updatedIdList));
    setIsAddingAll(false);
  };

  const isAvailable = (product: ApiProduct) => {
    if (!requiredUOM) {
      return true;
    }
    if (requiredUOM === RewardsUom.Dollars) {
      return true;
    }
    return !product.purchaseUom.length
      || ConversionUtility.isAnyConvertible(product.purchaseUom, requiredUOM);
  };

  const getFilters = (): Filter[] => {
    const filters: Filter[] = [
      {
        id: 'category-id',
        label: 'Category',
        options: categoryOptions,
        selectionMethod: 'single-select',
      },
    ];
    if (categoryIdFilterSelections.length === 1) {
      const selectedCategoryId = categoryIdFilterSelections[0];
      const category = categoryList?.find(c => c.id === selectedCategoryId);
      const subcategoryOptions = category?.masterSubcategories?.map((subcategory) => ({
        id: subcategory.id,
        label: subcategory.name,
      })).sort((a, b) => a.label.localeCompare(b.label)) ?? [];
      if (subcategoryOptions.length > 0) {
        filters.push({
          id: 'subcategory-id',
          label: 'Subcategory',
          options: subcategoryOptions,
          selectionMethod: 'single-select',
        });
      }
    }
    if (!requiredManufacturerID) {
      filters.push({
        id: 'manufacturer-id',
        label: 'Manufacturer',
        options: [
          {
            id: 'null',
            label: 'Unassigned',
          },
          ...manufacturerList?.map((manufacturer) => ({
            id: manufacturer.id,
            label: manufacturer.name ?? manufacturer.id,
          })).sort((a, b) => a.label.localeCompare(b.label)) ?? [],
        ],
        selectionMethod: 'single-select',
      });
    }
    return filters;
  };

  const hasUnavailableProducts = !!products?.data.some(p => !isAvailable(p));

  useEffect(() => {
    setPage(0);
  }, [
    debouncedSearch,
    filterSelections,
  ]);

  return (
    <Container maxWidth="lg">
      <VSpacer size="6"/>
      <Stack direction="row" justifyContent="space-between">
        <Toolbar
          filterSelections={filterSelections}
          filters={getFilters()}
          infoTooltip={{
            labelText: "Product unavailable",
            tooltipText: `Products that are listed as unavailable are not eligible to be added to 
              this promotion. This is due to none of the product's UOMs matching the UOM that is
              selected within the promotion loyalty value. For these products, we are
              unable to calculate a loyalty value conversion.`,
            visible: hasUnavailableProducts,
          }}
          onChange={({ search, selections }) => {
            setFilterSelections(selections ?? new Map());
            setSearch(search ?? '');
          }}
          retainSelectionsOnFilterChange
          testID="product-list-toolbar"
          totalItems={products?.total ?? 0}
          totalUnitLabel={{
            plural: t($ => $.common.units.products),
            singular: t($ => $.common.units.product),
          }}
        />
        <Stack alignItems="end" direction="column" justifyContent="space-between">
          {!!onAddedProductsButtonClicked && (
            <Stack>
              <VSpacer size="3"/>
              <Button
                disabled={!selectedIds.length}
                loading={isAddingAll}
                onClick={onAddedProductsButtonClicked}
                startIcon={<img src={GroupIcon} style={{ height: '18px', width: '18px' }}/>}
                testID="add-all-products-button"
                variant="contained"
              >
                Added products ({selectedIds.length})
              </Button>
            </Stack>
          )}
          <Button
            disabled={disabled || !products?.total}
            loading={isAddingAll}
            onClick={async () => {
              await addAll();
            }}
            startIcon={<AddIcon/>}
            testID="add-all-button"
            variant="text"
          >
            Add all
          </Button>
        </Stack>
      </Stack>
      <VSpacer size="5"/>
      {products?.data.map((product) => (
        <React.Fragment key={product.id}>
          <ProductCard
            disabled={disabled}
            onAction={(action: Action) => {
              const updatedSelectedIds = new Set(selectedIds);
              if (action === Action.AddAll) {
                updatedSelectedIds.add(product.id);
              } else {
                updatedSelectedIds.delete(product.id);
              }
              onChange(Array.from(updatedSelectedIds));
            }}
            product={product}
            promotions={getPromotions(product.id, promotions?.data ?? [])}
            selectedIds={selectedIds}
            unavailable={!isAvailable(product)}
          />
          <VSpacer size="4"/>
        </React.Fragment>
      ))}
      <VSpacer size='8'/>
      {isLoading ? (
        <Stack
          alignItems='center'
          direction='column'
          justifyContent='center'
          sx={{ marginTop: '10rem' }}
        >
          <CircularProgress/>
        </Stack>
      ) : (
        <>
          {products && !!products.lastPage && (
            <Stack
              alignItems='center'
              direction='row'
              justifyContent='center'
            >
              <Pagination
                count={products.lastPage + 1}
                onChange={(_, page) => {
                  setPage(page - 1);
                }}
                page={products.page + 1}
              />
            </Stack>
          )}
        </>
      )}
    </Container>
  );
};
